'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Button } from '@savvy-kit/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@savvy-kit/components/dialog';
import SearchableCombobox from '@savvy-kit/patterns/searchable-combobox';
import Tag from '@savvy-kit/patterns/tag';
import { toast } from 'sonner';
import { graphql } from 'src/gql';
import { tagIndicatorPropsFromTagData } from 'src/utils/advisor-dash/tag-indicator-from-tag-data';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import { ADVISOR_DASH_HOUSEHOLD_QUERY } from '../layout/client-household-layout';
import { CategoryIndicator } from './category-indicator';

export type SimpleTag = {
  readonly id: string;
  readonly name: string;
  readonly hexColorCode?: string | null;
  readonly emoji?: string | null;
  readonly isSmart?: boolean;
};

type ManageHouseholdTagsDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly householdId: string;
  readonly householdName?: string;
  readonly tags: readonly SimpleTag[];
  readonly suggestions?: readonly SimpleTag[];
  readonly availableTags?: readonly SimpleTag[];
  readonly onSave?: (nextTagIds: readonly string[]) => Promise<void> | void;
};

const EMPTY_TAGS: readonly SimpleTag[] = [];

const UpdateHouseholdTagsMutation = graphql(/* GraphQL */ `
  mutation UpdateHouseholdTags($input: UpdateHouseholdTagsInput!) {
    updateHouseholdTags(input: $input) {
      household {
        id
        householdTags {
          id
          organizationHouseholdTag {
            id
            name
            organizationHouseholdCategory {
              hexColorCode
              emoji
            }
          }
        }
      }
    }
  }
`);

export function ManageHouseholdTagsDialog({
  open,
  onOpenChange,
  householdId,
  householdName,
  tags,
  suggestions = EMPTY_TAGS,
  availableTags = EMPTY_TAGS,
  onSave,
}: ManageHouseholdTagsDialogProps) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(tags.map((t) => t.id)));
  const [isSaving, setIsSaving] = useState(false);

  const client = useApolloClient();
  const [updateHouseholdTags] = useMutationWithErrorLogging(UpdateHouseholdTagsMutation);

  // Reset selected tags when tags prop changes (when dialog opens with new data)
  useEffect(() => {
    if (open) {
      setSelected(new Set(tags.map((t) => t.id)));
    }
  }, [tags, open]);

  const allOptions: readonly SimpleTag[] = useMemo(() => {
    if (availableTags.length > 0) return availableTags;
    const map = new Map<string, SimpleTag>();
    [...tags, ...suggestions].forEach((t) => {
      if (!map.has(t.id)) map.set(t.id, t);
    });
    return Array.from(map.values());
  }, [availableTags, tags, suggestions]);

  const currentTags = useMemo(
    () => allOptions.filter((t) => selected.has(t.id)).sort((a, b) => a.name.localeCompare(b.name)),
    [allOptions, selected],
  );

  // Options for SearchableCombobox - exclude already selected AND smart tags.
  // Smart tags are rule-driven; advisors can't add or remove them manually
  // (BE enforces via SyncHouseholdTags). Hiding them from the picker prevents
  // confusion.
  const selectOptions = useMemo(
    () =>
      allOptions
        .filter((t) => !selected.has(t.id) && t.isSmart !== true)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((t) => ({
          value: t.id,
          label: t.name,
        })),
    [allOptions, selected],
  );

  const findTag = (tagId: string) => allOptions.find((t) => t.id === tagId);

  const visibleSuggestions = useMemo(() => {
    return suggestions
      .filter((s) => !selected.has(s.id) && s.isSmart !== true)
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 12);
  }, [suggestions, selected]);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleAddTag = useCallback((tagId: string) => {
    if (tagId !== '') {
      setSelected((prev) => new Set(prev).add(tagId));
    }
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const tagIds = Array.from(selected);

      await updateHouseholdTags({
        variables: {
          input: {
            householdId,
            organizationHouseholdTagIds: tagIds,
          },
        },
        refetchQueries: [ADVISOR_DASH_HOUSEHOLD_QUERY],
      });

      // Evict the 'households' field from affected tags in the cache.
      const affectedTagIds = new Set([...tags.map((t) => t.id), ...tagIds]);
      affectedTagIds.forEach((tagId) => {
        client.cache.evict({
          id: client.cache.identify({ __typename: 'OrganizationHouseholdTag', id: tagId }),
          fieldName: 'households',
        });
      });
      client.cache.gc();

      await onSave?.(tagIds);
      toast.success('Tags updated successfully');
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating household tags:', error);
      handleMutationError(error, 'Failed to update tags');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover" initialFocus={false}>
        <DialogTitle className="text-foreground">Manage household tags</DialogTitle>

        <div className="mt-3">
          <DialogDescription className="text-secondary mb-2">
            {`${householdName ?? ''} Tags (${currentTags.length})`}
          </DialogDescription>

          <div className="border border-light rounded-md p-3 min-h-[60px]">
            <div className="flex flex-wrap gap-2">
              {currentTags.length === 0 ? (
                <span className="text-secondary text-sm">No tags selected</span>
              ) : (
                currentTags.map((t) => {
                  const isSmart = t.isSmart === true;
                  // Smart tags are rule-driven — show the chip but make it
                  // un-dismissible. The advisor changes the rule (or the
                  // underlying data) to remove the tag, not this dialog.
                  // For SR users, encode that meaning in aria-label since the
                  // absence of an X icon is not perceivable (WCAG 1.3.3).
                  const accessibleLabel = isSmart
                    ? `${t.name} — smart tag, managed by rule, cannot be removed here`
                    : undefined;
                  return (
                    <Tag
                      key={t.id}
                      {...tagIndicatorPropsFromTagData(t)}
                      type="filter"
                      dismissible={!isSmart}
                      onDismiss={!isSmart ? () => toggle(t.id) : undefined}
                      aria-label={accessibleLabel}
                      className="rounded-sm"
                    >
                      {t.name}
                    </Tag>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-3">
          <SearchableCombobox
            id="add-tag-select"
            options={selectOptions}
            value=""
            onValueChange={handleAddTag}
            placeholder="Search to add tags"
            emptyMessage="No results found"
            renderOption={(option) => {
              const tag = findTag(option.value);
              return (
                <div className="flex items-center justify-start gap-2">
                  <CategoryIndicator hexColorCode={tag?.hexColorCode} emoji={tag?.emoji} placeholder />
                  <span className="truncate">{option.label}</span>
                </div>
              );
            }}
          />

          <div>
            <div className="text-secondary text-xs font-medium mb-2">SUGGESTIONS</div>
            <div className="flex flex-wrap gap-2">
              {visibleSuggestions.length === 0 ? (
                <span className="text-secondary text-sm">No suggestions available</span>
              ) : (
                visibleSuggestions.map((t) => (
                  <button key={t.id} onClick={() => toggle(t.id)} type="button" className="inline-block">
                    <Tag
                      {...tagIndicatorPropsFromTagData(t)}
                      type="selectable"
                      className="rounded-sm hover:cursor-pointer"
                    >
                      {t.name}
                    </Tag>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <DialogClose
            render={
              <Button variant="secondary" disabled={isSaving}>
                Cancel
              </Button>
            }
          />
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
